// modelVisibility — may this project's model be offered to the user yet?
//
// One predicate, in one place, because the surfaces that ask do not
// agree on how they ask. ProjectItemPersonalArea gates the Model info
// block on `zipInfo` being truthy and the View Model BUTTON on
// `zipInfo.paths.length > 0`; CreateIssuePage gates on a `.glb` regex
// over the same paths. Gating one and not the others produces the worst
// available outcome - a visible button leading to an empty viewer.
//
// THE RULE IS FAIL OPEN, AND THAT IS THE WHOLE DESIGN.
//
// Hide the model only when the server has said, explicitly, that an
// upload is in flight. Missing, null, undefined and unrecognised all
// mean SHOW. `modelStatus` arrived on 2026-08-30; the 545 projects that
// already had a model predate it, and lean() queries do not apply
// mongoose schema defaults, so `undefined` is the normal reading for a
// perfectly healthy legacy project. Requiring `=== "live"` here would
// blank the model button across production.
//
// Chunked/streaming export is a memory strategy for models too large for
// one session, not a product tier. A project that reaches a viewable
// model through POST /projects/export with no manifest, no tiles and no
// ModelVersion row is finished, not pending, and always will be.
export function isModelUploading(project) {
  return (project && project.modelStatus) === "uploading";
}

// A model that is being served while documents are still landing.
//
// Distinct from both neighbours and it must LOOK distinct: 'uploading'
// hides, 'partial' shows WITH a label, 'live' shows as whole. Showing a
// partial model unlabelled is the original 2026-08-30 bug wearing a
// different hat - a viewer being told a fraction of a building is the
// building. Never render a partial model without modelCoverageLabel.
export function isModelPartial(project) {
  return (project && project.modelStatus) === "partial";
}

// "3 of 20 documents", or "" when there is nothing meaningful to say.
// Deliberately returns empty rather than a guess: a partial model whose
// counts did not arrive should say "Partial" alone rather than invent a
// denominator.
export function modelCoverageLabel(project) {
  if (!project) return "";
  const done = Number(project.modelDocumentsComplete);
  const all = Number(project.modelDocumentsExpected);
  if (!Number.isFinite(done) || !Number.isFinite(all) || all <= 0) return "";
  return `${done} of ${all} documents`;
}

// Is there a model to show at all? Mirrors the historical `zipInfo ? …`
// test, plus the gate.
export function projectHasVisibleModel(project) {
  if (!project || !project.zipInfo) return false;
  return !isModelUploading(project);
}

// The stricter form CreateIssuePage needs: a STREAMABLE model, i.e. one
// whose paths carry geometry.
const GLB_PATH_RE = /\.glb(\.gz)?$/i;

export function projectHasStreamableModel(project) {
  if (!projectHasVisibleModel(project)) return false;
  const paths = (project.zipInfo && project.zipInfo.paths) || [];
  return paths.some((p) => GLB_PATH_RE.test(String(p)));
}

// How many model files an export has. A slim projects-list row
// (api/projects/getProjects) carries `pathsCount` instead of `paths`.
export function modelFileCount(zipInfo) {
  if (!zipInfo) return 0;
  if (Array.isArray(zipInfo.paths)) return zipInfo.paths.length;
  return Number(zipInfo.pathsCount) || 0;
}

export default projectHasVisibleModel;
